import { useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import {
  registerContactSupport,
  showFailureToast,
} from "@-/dev.improve.haniverse.components.ui.failure-toast";
import { isStudyDataUnavailableError } from "@-/dev.improve.haniverse.lib.error-classification";
import { useSupportSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.support.navigator";
import useLatest from "@-/com.npmjs.use-latest";
import { useEffect } from "react";

/**
 * Tells the user their study data is unavailable exactly when they try to use it, and never
 * otherwise. A failed study database is rare: the rest of the app (browsing, search, reference
 * data) keeps working, and only an action that needs study data — a write, a navigation whose
 * destination reads it, "Start Reviewing" — surfaces the shared "contact support" notice.
 *
 * Renders nothing. It owns the support navigator the notice's action opens (direct router
 * navigation is banned) and reports failed writes, which otherwise only reach the logs.
 */
export function StudyDataFailureNotifier() {
  const queryClient = useQueryClient();
  const supportNavigator = useSupportSettingsPageNavigator();
  const latestSupportNavigator = useLatest(supportNavigator);

  useEffect(
    () =>
      registerContactSupport(() => {
        latestSupportNavigator.current.push.mutate();
      }),
    [latestSupportNavigator],
  );

  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "error" &&
          isStudyDataUnavailableError(event.action.error)
        ) {
          // Same toast id for every study-data failure, so a burst of writes is one notice.
          showFailureToast(event.action.error);
        }
      }),
    [queryClient],
  );

  return null;
}
